import React, {useMemo} from "react";
import Paper from "@mui/material/Paper";
import {Col, Row} from "reactstrap";
import {ReactComponent as SuccessSvg} from "../../../../../../../../assets/images/art-like/success.svg";
import {ReactComponent as FailedSvg} from "../../../../../../../../assets/images/art-like/failed.svg";
import {ReactComponent as ProblemSvg} from "../../../../../../../../assets/images/art-like/problem.svg";
import {ReactComponent as TryingSvg} from "../../../../../../../../assets/images/art-like/trying.svg";
import {CircularProgress, LinearProgress, SvgIcon, Tooltip} from "@mui/material";
import Button from "../../../../../../../components/base/button/button";
import {ReactComponent as Excel} from "../../../../../../../../assets/images/excel.svg";


const statuses = {
    trying: 'trying',
    success: 'success',
    failed: 'failed',
    incomplete: 'incomplete',
}

const BulkPriceChangeReportAttemptSummary = ({
                                                 attempt,
                                                 summaryInfo,
                                                 trying,
                                                 starting,
                                                 cancelling,
                                                 exporting,
                                                 readonly,
                                                 start,
                                                 cancel,
                                                 pause,
                                                 exportReport,
                                                 canExportReport = false,
                                             }) => {
    const hasFailedParts = useMemo(() => (attempt?.failedParts ?? 0) > 0, [attempt?.failedParts])
    const hasSuccessfulParts = useMemo(() => (attempt?.successfulParts ?? 0) > 0, [attempt?.successfulParts])

    const status = useMemo(() => {
        if (!readonly && trying)
            return statuses.trying
        if (attempt?.affectedParts === attempt?.successfulParts)
            return statuses.success
        if (attempt?.affectedParts === attempt?.failedParts)
            return statuses.failed
        return statuses.incomplete
    }, [attempt?.affectedParts, attempt?.failedParts, attempt?.successfulParts, readonly, trying])

    const attemptedParts = useMemo(() => (attempt?.successfulParts + attempt?.failedParts) ?? 0, [attempt?.failedParts, attempt?.successfulParts]);
    const progressValue = useMemo(() => Math.round((attemptedParts / (attempt?.affectedParts ?? 1)) * 100), [attemptedParts, attempt?.affectedParts])
    const progressBuffer = useMemo(() => Math.min(progressValue + 1, 100), [progressValue])

    const allPartsAttempted = useMemo(() => attemptedParts === attempt?.affectedParts, [attempt?.affectedParts, attemptedParts])
    const noPartsAttempted = useMemo(() => attemptedParts === 0, [attemptedParts])

    return (
        <>
            <Paper className={'paper bulk-price-change-report-view-summary'}>
                <Row className={'w-100 mb-5'}>
                    <Col xs={12} lg={6} className={'info-section'}>
                        <div className={'general-info-section'}>
                            <div className={'info'}>
                                <p>Submit Date & Time:</p>
                                <div>
                                    <p>
                                        {summaryInfo.submitDateTime}
                                    </p>
                                </div>
                            </div>
                            <div className={'info'}>
                                <p>Operator Name:</p>
                                <div>
                                    <p>
                                        {summaryInfo.operatorName}
                                    </p>
                                </div>
                            </div>
                            <div className={'info'}>
                                <p>Price Change:</p>
                                <div>
                                    <p>
                                        {summaryInfo.priceChange}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className={'price-levels'}>
                            <p className={'title'}>
                                {`Affected Price Levels (${summaryInfo?.priceLevels?.length ?? 0})`}
                            </p>
                            <div className={'d-flex flex-wrap w-100'}>
                                {
                                    summaryInfo?.priceLevels?.map(priceLevel => (
                                        <div className={'bubble-card'} key={priceLevel.id}>
                                            <p>
                                                {priceLevel.title ?? ''}
                                            </p>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </Col>
                    <Col xs={12} lg={6} className={'detail-section'}>
                        {status === statuses.success && <SuccessSvg/>}
                        {status === statuses.failed && <FailedSvg/>}
                        {status === statuses.incomplete && <ProblemSvg/>}
                        {
                            status === statuses.trying &&
                            <>
                                <TryingSvg/>
                                <Tooltip
                                    open
                                    placement={'top'}
                                    title={
                                        <p className={'text-center'}>
                                            {`${progressValue}%`} <br/>
                                            {`${attemptedParts} / ${attempt?.affectedParts ?? 0}`}
                                        </p>
                                    }
                                    arrow>
                                    <LinearProgress
                                        className={'w-78 mt-4'}
                                        variant="buffer"
                                        value={progressValue}
                                        valueBuffer={progressBuffer}
                                    />
                                </Tooltip>
                            </>
                        }
                        <div className={'report-legend'}>
                            <p>
                                {status === statuses.success && "All the parts have been changed successfully"}
                                {status === statuses.failed && "All the parts have been failed, please try again"}
                                {status === statuses.incomplete && "Some parts of the request were not updated"}
                                {status === statuses.trying && "We are updating the requested parts price, it may take a few minutes. Please be patient, Thanks"}
                            </p>
                        </div>
                        {
                            status !== statuses.trying &&
                            <div className={'report-table'}>
                                <div className={'report-table-requested-part'}>
                                    <p>requested parts</p>
                                    <p>{attempt?.affectedParts ?? 0}</p>
                                </div>
                                <div className={'report-table-success'}>
                                    <p>Successful Parts</p>
                                    <p>{attempt?.successfulParts ?? 0}</p>
                                </div>
                                <div className={'report-table-failed'}>
                                    <p>Failed Parts</p>
                                    <p>{attempt?.failedParts ?? 0}</p>
                                </div>
                            </div>
                        }
                        {
                            !readonly && !trying && status !== statuses.success &&
                            <Button
                                className='summary-action'
                                onClick={() => start()}
                                disabled={starting}
                                variant={'outlined'}
                                color={'primary'}
                            >
                                {
                                    starting
                                        ? "Processing..."
                                        : (hasFailedParts || hasSuccessfulParts)
                                            ? !allPartsAttempted
                                                ? "Resume this attempt"
                                                : "Try again for failed products"
                                            : "Start this Attempt"
                                }

                            </Button>
                        }
                        {
                            !readonly && !trying && !allPartsAttempted && !noPartsAttempted &&
                            <Button
                                className='summary-action'
                                onClick={() => cancel()}
                                disabled={cancelling}
                                variant={'outlined'}
                                color={'primary'}
                            >
                                {
                                    cancelling
                                        ? "Processing..."
                                        : "Close this Attempt"
                                }
                            </Button>
                        }
                        {
                            canExportReport && (!trying || readonly) && !noPartsAttempted &&
                            <Button
                                className='summary-action'
                                onClick={() => exportReport(attempt, null)}
                                disabled={exporting}
                                variant={'text'}
                                color={'primary'}
                            >
                                {
                                    exporting
                                        ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/>
                                        : <SvgIcon className='icon normal mr-2' component={Excel}/>
                                }
                                {
                                    exporting
                                        ? "Downloading..."
                                        : "Download All Items Report"
                                }
                            </Button>
                        }
                        {
                            canExportReport && (!trying || readonly) && hasFailedParts &&
                            <Button
                                className='summary-action'
                                onClick={() => exportReport(attempt, false)}
                                disabled={exporting}
                                variant={'text'}
                                color={'primary'}
                            >
                                {
                                    exporting
                                        ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/>
                                        : <SvgIcon className='icon normal mr-2' component={Excel}/>
                                }
                                {
                                    exporting
                                        ? "Downloading..."
                                        : "Download Failed Items Report"
                                }
                            </Button>
                        }
                        {
                            canExportReport && (!trying || readonly) && hasSuccessfulParts &&
                            <Button
                                className='summary-action'
                                onClick={() => exportReport(attempt, true)}
                                disabled={exporting}
                                variant={'text'}
                                color={'primary'}
                            >
                                {
                                    exporting
                                        ? <CircularProgress size={15} color={'primary'} className={'mr-2'}/>
                                        : <SvgIcon className='icon normal mr-2' component={Excel}/>
                                }
                                {
                                    exporting
                                        ? "Downloading..."
                                        : "Download Successful Items Report"
                                }
                            </Button>
                        }
                        {
                            !readonly && trying &&
                            <div className={'d-flex justify-content-around align-items-center w-78'}>
                                <Button
                                    className='summary-action px-3 mr-2'
                                    onClick={() => pause()}
                                    variant={'outlined'}
                                    color={'primary'}
                                >
                                    Pause
                                </Button>
                                <Button
                                    className='summary-action px-3 ml-2'
                                    onClick={() => cancel()}
                                    disabled={cancelling}
                                    variant={'outlined'}
                                    color={'primary'}
                                >
                                    {
                                        cancelling
                                            ? "Processing..."
                                            : "Cancel"
                                    }
                                </Button>
                            </div>
                        }
                    </Col>
                </Row>
            </Paper>
        </>
    );
}

export default BulkPriceChangeReportAttemptSummary;
